import React, { useCallback } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import * as R from "ramda";

import Icon from "components/Icon";
import { ButtonDiv, MenuDiv } from "components/accessibility/Div";
import { useCurrentTenant } from "contexts/currentTenant";
import { useLocale, useSetLocale } from "contexts/locale";
import { getActiveLanguages } from "reducers/tenant";

import styles from "./LanguagePicker.scss";

const Language = ({ name, locale, isCurrent }) => {
  const setLocale = useSetLocale();
  const onChange = useCallback(() => setLocale(locale), [locale, setLocale]);

  return (
    <ButtonDiv
      key={locale}
      role="menuitem"
      data-locale={locale}
      className={classnames(styles.language, {
        [styles.current]: isCurrent,
      })}
      onMouseDown={isCurrent ? null : onChange}
    >
      {name}
    </ButtonDiv>
  );
};
Language.propTypes = {
  name: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  isCurrent: PropTypes.bool.isRequired,
};

const MobileLanguagePicker = ({ children }) => (
  <div className={styles.mobilePicker}>
    <div>
      <Icon icon="globe" size="lg" inverse />
    </div>
    <div className={styles.languages}>{children}</div>
  </div>
);

const DesktopLanguagePicker = ({ children, currentLanguageName }) => {
  const MenuLabel = (
    <div className={styles.menuLabel}>
      <Icon icon="globe" size="lg" inverse />
      <span className={styles.currentLanguage}>{currentLanguageName}</span>
      <Icon icon="caret-down" size="sm" inverse />
    </div>
  );

  return (
    <MenuDiv className={styles.desktopPicker} label={MenuLabel}>
      <div className={styles.dropdown}>
        <div className={styles.arrow} />
        {children}
      </div>
    </MenuDiv>
  );
};
DesktopLanguagePicker.propTypes = {
  currentLanguageName: PropTypes.string.isRequired,
};

const useLanguage = () => {
  const locale = useLocale();
  const { languages } = useCurrentTenant();
  return R.compose(
    R.defaultTo({ name: "English", locale: "en" }),
    R.find(R.propEq(locale, "locale")),
  )(languages);
};

const LanguagePicker = () => {
  const tenant = useCurrentTenant();
  const languages = getActiveLanguages(tenant);
  const { locale: currentLocale, name: currentName } = useLanguage();
  if (languages.length <= 1) return null;

  const languageButtons = languages.map(({ locale, name }) => {
    const isCurrent = locale === currentLocale;
    return <Language key={locale} name={name} locale={locale} isCurrent={isCurrent} />;
  });
  return (
    <div className={styles.container} data-language-picker>
      <DesktopLanguagePicker currentLanguageName={currentName}>
        {languageButtons}
      </DesktopLanguagePicker>
      <MobileLanguagePicker>{languageButtons}</MobileLanguagePicker>
    </div>
  );
};

export default LanguagePicker;
